'use client';

import { createContext, ReactNode, useContext } from "react";
import {
    useSingleTypeEditor,
    UseSingleTypeEditorType
} from "@/app/lib/hooks/use-editor/useSingleTypeEditor";
import { SiteCollection } from "@/app/lib/generated/graphql-types";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import { toggleDraftMode } from "@/app/lib/actions/editing";
import FloatingEditorMenu from "@/app/features/shared/__to_be_integrated/FloatingEditorMenu";
import { getSiteCollection } from "@/app/features/sites/collection/data-access/queries";
import { updateSiteCollection } from "@/app/features/sites/collection/data-access/mutation";
import { SiteCollectionSchema } from "@/app/features/sites/collection/types/schema";
import { SiteCollectionEditable } from "@/app/features/sites/collection/types/types";

const SiteCollectionEditorContext = createContext<UseSingleTypeEditorType<SiteCollectionEditable> | undefined>(undefined);

export function useSiteCollectionEditor() {
    const context = useContext(SiteCollectionEditorContext);
    if (!context) {
        throw new Error("useSiteCollectionEditor must be used within a SiteCollectionEditorProvider");
    }
    return context;
}

interface SiteCollectionEditorProviderProps {
    children: ReactNode;
    isDraftModeEnabled: boolean;
}

export default function SiteCollectionEditorProvider({ children, isDraftModeEnabled }: SiteCollectionEditorProviderProps) {
    const editor = useSingleTypeEditor<SiteCollection, SiteCollectionEditable, SiteCollectionEditable>({
        schema: SiteCollectionSchema,
        loadEntity: getSiteCollection,
        update: updateSiteCollection,
        toInput: (editable) => editable,
    });

    if (editor.isLoading) return <LoadingSpinner />


    return (
        <SiteCollectionEditorContext.Provider value={editor}>
            {children}
            <FloatingEditorMenu
                autosaveState={editor.autosaveState}
                isDraftModeEnabled={isDraftModeEnabled}
                toggleDraftMode={toggleDraftMode}
                hasChanges={editor.hasChanges}
                onPublish={editor.publish}
                onDiscard={editor.discardChanges}
            />
        </SiteCollectionEditorContext.Provider>
    )
}
